/**
 * Fleet masters: vehicles, drivers, routes.
 *
 * Set up once and then rarely touched, so this screen optimises for
 * being understandable rather than fast. The one thing it insists on is
 * that a hired truck names its owner -- a subcontracted vehicle with no
 * subcontractor produces trips that generate a cost nobody can be paid
 * for, and that is a hole you find at month end.
 */
import { useState } from 'react'
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  createDriver, createRoute, createVehicle, fetchDrivers, fetchGroups,
  fetchRoutes, fetchVehicles, setVehicleActive, setVehicleCostGroup,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { c, fmtMoney } from '../theme'
import SectionHead from '../components/SectionHead'
import { normalise, sanitize } from '../vouchers/money'
import ScreenTitle from '../components/ScreenTitle'
import SubcontractorPicker from './SubcontractorPicker'

export default function Fleet() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const qc = useQueryClient()
  const mayEdit = can(P.transportMasters)
  const [error, setError] = useState<string | null>(null)

  const vehicles = useQuery({ queryKey: ['vehicles'],
    queryFn: () => fetchVehicles(true) })
  const drivers = useQuery({ queryKey: ['drivers'],
    queryFn: () => fetchDrivers(true) })
  const routes = useQuery({ queryKey: ['routes'],
    queryFn: () => fetchRoutes(true) })
  // Expense groups only: a truck's costs live under one of them.
  const groups = useQuery({ queryKey: ['groups'], queryFn: fetchGroups,
    enabled: mayEdit })
  const expenseGroups = (groups.data ?? []).filter((g) => g.nature === 'EXPENSE')

  const linkCosts = useMutation({
    mutationFn: (p: { id: number; code: string | null }) =>
      setVehicleCostGroup(p.id, p.code),
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['vehicles'] })
      void qc.invalidateQueries({ queryKey: ['profitability'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  // -- vehicle form -------------------------------------------------------
  const [plate, setPlate] = useState('')
  const [desc, setDesc] = useState('')
  const [kind, setKind] = useState<'OWN' | 'SUBCONTRACTED'>('OWN')
  const [sub, setSub] = useState('')

  const addVehicle = useMutation({
    mutationFn: () => createVehicle({
      plate_no: plate.trim(), description: desc.trim() || null, kind,
      subcontractor_code: kind === 'SUBCONTRACTED' ? sub : null }),
    onSuccess: () => {
      setPlate(''); setDesc(''); setSub(''); setError(null)
      void qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  // -- driver form --------------------------------------------------------
  const [dName, setDName] = useState('')
  const [dPhone, setDPhone] = useState('')
  const addDriver = useMutation({
    mutationFn: () => createDriver({ name: dName.trim(),
                                     phone: dPhone.trim() || null }),
    onSuccess: () => {
      setDName(''); setDPhone(''); setError(null)
      void qc.invalidateQueries({ queryKey: ['drivers'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  // -- route form ---------------------------------------------------------
  const [rFrom, setRFrom] = useState('')
  const [rTo, setRTo] = useState('')
  const [rRate, setRRate] = useState('')
  const addRoute = useMutation({
    mutationFn: () => createRoute({
      from_place: rFrom.trim(), to_place: rTo.trim(),
      default_rate: rRate.trim() ? normalise(rRate) : null }),
    onSuccess: () => {
      setRFrom(''); setRTo(''); setRRate(''); setError(null)
      void qc.invalidateQueries({ queryKey: ['routes'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const toggleVehicle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      setVehicleActive(id, active),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vehicles'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Stack spacing={2}>
      <ScreenTitle titleKey="nav.fleet" inset={false} sub={t('fleet.sub')} />
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Vehicles */}
      <Paper variant="outlined">
        <SectionHead label={t('fleet.vehicles')}
          right={<Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {vehicles.data?.length ?? 0}
          </Typography>} />

        {mayEdit && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
            sx={{ px: 2, py: 2, alignItems: { md: 'flex-end' } }}>
            <TextField size="small" label={t('fleet.plate')} value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              sx={{ width: 150 }} />
            <TextField size="small" label={t('fleet.description')} value={desc}
              onChange={(e) => setDesc(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" select label={t('fleet.kind')} value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              sx={{ width: 170 }}>
              <MenuItem value="OWN">{t('fleet.own')}</MenuItem>
              <MenuItem value="SUBCONTRACTED">{t('fleet.hired')}</MenuItem>
            </TextField>
            {kind === 'SUBCONTRACTED' && (
              <SubcontractorPicker value={sub} onChange={setSub} />
            )}
            <Button variant="outlined" startIcon={<AddIcon />}
              disabled={!plate.trim() || addVehicle.isPending
                        || (kind === 'SUBCONTRACTED' && !sub)}
              onClick={() => addVehicle.mutate()}>
              {t('common.add')}
            </Button>
          </Stack>
        )}

        <Box sx={{ borderTop: `1px solid ${c.border}` }}>
          {(vehicles.data ?? []).map((v) => (
            <Stack key={v.id} direction="row" alignItems="center" flexWrap="wrap"
              useFlexGap columnGap={2} rowGap={1}
              sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${c.divider}`,
                    opacity: v.is_active ? 1 : 0.5 }}>
              <Typography className="num" sx={{ width: 110, fontSize: 13, textAlign: 'start',
                                                 whiteSpace: 'nowrap' }}>
                {v.plate_no}
              </Typography>
              {/* On a phone the plate and description take the first line
                  and the cost group drops below, at full width. */}
              <Typography sx={{ flex: '1 1 140px', fontSize: 13 }}>
                {v.description ?? '—'}
              </Typography>
              {v.kind === 'SUBCONTRACTED' ? (
                <Chip size="small" label={`${t('fleet.hired')} · ${v.subcontractor_code ?? ''}`}
                  sx={{ height: 19, fontSize: 10, bgcolor: c.warningSoft,
                        color: c.warningInk }} />
              ) : (
                <Chip size="small" label={t('fleet.own')}
                  sx={{ height: 19, fontSize: 10, bgcolor: c.primarySoft,
                        color: c.primaryInk }} />
              )}
              {/* Where this truck's own costs are booked. Profit per
                  truck adds up everything under it. */}
              {mayEdit ? (
                <TextField select size="small" value={v.cost_group_code ?? ''}
                  label={t('fleet.costGroup')}
                  sx={{ width: { xs: 'calc(100% - 96px)', sm: 230 } }}
                  onChange={(e) => linkCosts.mutate(
                    { id: v.id, code: e.target.value || null })}>
                  <MenuItem value="">{t('fleet.noCostGroup')}</MenuItem>
                  {expenseGroups.map((g) => (
                    <MenuItem key={g.code} value={g.code}>{g.name_en}</MenuItem>
                  ))}
                  {v.cost_group_code && !expenseGroups.some(
                    (g) => g.code === v.cost_group_code) && (
                    <MenuItem value={v.cost_group_code}>{v.cost_group_name}</MenuItem>
                  )}
                </TextField>
              ) : v.cost_group_name ? (
                <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                  {v.cost_group_name}
                </Typography>
              ) : null}
              {mayEdit && (
                <Button size="small" sx={{ color: c.inkMuted }}
                  onClick={() => toggleVehicle.mutate(
                    { id: v.id, active: !v.is_active })}>
                  {v.is_active ? t('fleet.retire') : t('fleet.restore')}
                </Button>
              )}
            </Stack>
          ))}
          {(vehicles.data ?? []).length === 0 && (
            <Typography variant="body2" sx={{ px: 2, py: 3, color: c.inkMuted }}>
              {t('fleet.noVehicles')}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Routes */}
      <Paper variant="outlined">
        <SectionHead label={t('fleet.routes')} />
        {mayEdit && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}
            sx={{ px: 2, py: 2, alignItems: { md: 'flex-end' } }}>
            <TextField size="small" label={t('fleet.from')} value={rFrom}
              onChange={(e) => setRFrom(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label={t('fleet.to')} value={rTo}
              onChange={(e) => setRTo(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label={t('fleet.houseRate')} value={rRate}
              onChange={(e) => setRRate(sanitize(e.target.value))}
              helperText={t('fleet.houseRateHint')}
              sx={{ width: 180 }} />
            <Button variant="outlined" startIcon={<AddIcon />}
              disabled={!rFrom.trim() || !rTo.trim() || addRoute.isPending}
              onClick={() => addRoute.mutate()}>
              {t('common.add')}
            </Button>
          </Stack>
        )}
        <Box sx={{ borderTop: `1px solid ${c.border}` }}>
          {(routes.data ?? []).map((r) => (
            <Stack key={r.id} direction="row" alignItems="center" spacing={2}
              sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${c.divider}` }}>
              <Typography sx={{ flex: 1, fontSize: 13 }}>
                {r.from_place} → {r.to_place}
              </Typography>
              <Typography className="num" sx={{ fontSize: 13 }}>
                {r.default_rate ? fmtMoney(r.default_rate) : '—'}
              </Typography>
            </Stack>
          ))}
          {(routes.data ?? []).length === 0 && (
            <Typography variant="body2" sx={{ px: 2, py: 3, color: c.inkMuted }}>
              {t('fleet.noRoutes')}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Drivers */}
      <Paper variant="outlined">
        <SectionHead label={t('fleet.drivers')} />
        {mayEdit && (
          <Stack direction="row" spacing={1.5}
            sx={{ px: 2, py: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField size="small" label={t('fleet.driverName')} value={dName}
              onChange={(e) => setDName(e.target.value)} sx={{ flex: 1,
                                                               minWidth: 200 }} />
            <TextField size="small" label={t('fleet.phone')} value={dPhone}
              onChange={(e) => setDPhone(e.target.value)} sx={{ width: 180 }} />
            <Button variant="outlined" startIcon={<AddIcon />}
              disabled={!dName.trim() || addDriver.isPending}
              onClick={() => addDriver.mutate()}>
              {t('common.add')}
            </Button>
          </Stack>
        )}
        <Box sx={{ borderTop: `1px solid ${c.border}` }}>
          {(drivers.data ?? []).map((d) => (
            <Stack key={d.id} direction="row" alignItems="center" spacing={2}
              sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${c.divider}` }}>
              <Typography sx={{ flex: 1, fontSize: 13 }}>{d.name}</Typography>
              <Typography className="num" sx={{ fontSize: 13, color: c.inkMuted }}>
                {d.phone ?? '—'}
              </Typography>
            </Stack>
          ))}
          {(drivers.data ?? []).length === 0 && (
            <Typography variant="body2" sx={{ px: 2, py: 3, color: c.inkMuted }}>
              {t('fleet.noDrivers')}
            </Typography>
          )}
        </Box>
      </Paper>
    </Stack>
  )
}
